import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Download, RefreshCw, Search, UserPlus, X } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  BackLink, Button, Card, CardTitle, CheckLabel, Checks, ChipClear, Container, Empty, Field, FilterChip, Header,
  Input, Message, Modal, ModalTitle, Overlay, Select, StatBar, StatPill, StatPillLabel, StatPillValue, Subtitle, Title,
} from '../admin/ui';
import * as Api from './PersonAdminApi';
import { PERMISSIONS, useCan } from '../authority/permissions';
import NewPersonForm from './NewPersonForm';
import {
  INTEREST_VALUES, PROFESSIONAL_STATUS_VALUES, errorText, eventTitle, interestLabel, originLabel, statusLabel,
} from './personAdminModel';

// The admin person list (plan §7.7 item 10, P11): one list replacing User Management and the
// speaker list. Search by name or email, a few filters (professional status, interest, has account,
// at an event), paging and a little sorting; the KPIs come from the statistics route; "Export CSV"
// downloads the same filtered list. "New person" creates one by hand (NewPersonForm: the duplicate
// check first, then the person's detail opens; no research starts by itself). Server-side: the directory service does the filtering and
// paging, so the screen holds only the query it sent. Desktop only (item 13).
//
// Every call goes through ./PersonAdminApi; a 4xx/5xx answers a plain-text body that is what the
// admin should read.

const SORT_OPTIONS = [
  { value: 'name', id: 'personAdmin.sort.name' },
  { value: 'email', id: 'personAdmin.sort.email' },
  { value: 'institution', id: 'personAdmin.sort.institution' },
  { value: 'country', id: 'personAdmin.sort.country' },
  { value: 'createdAt', id: 'personAdmin.sort.createdAt' },
  { value: 'updatedAt', id: 'personAdmin.sort.updatedAt' },
];

const SIZE = 25;

// The filter state as the API wants it: the empty choices are left out (PersonAdminApi.filterParams).
const apiFiltersOf = (filters) => ({
  search: filters.search,
  statuses: filters.statuses,
  interests: filters.interest ? [filters.interest] : [],
  hasAccount: filters.hasAccount === '' ? undefined : filters.hasAccount === 'true',
  eventId: filters.eventId ? Number(filters.eventId) : null,
});

const PersonList = () => {
  const can = useCan();
  const intl = useIntl();
  const { language } = useLanguage();
  const t = useCallback((id, def, values) => intl.formatMessage({ id, defaultMessage: def }, values), [intl]);

  const [filters, setFilters] = useState({ search: '', statuses: [], interest: '', hasAccount: '', eventId: '' });
  const [searchInput, setSearchInput] = useState('');
  const [sort, setSort] = useState('name');
  const [dir, setDir] = useState('asc');
  const [page, setPage] = useState(0);

  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();

  const apiFilters = useMemo(() => apiFiltersOf(filters), [filters]);
  const seq = useRef(0);

  const load = useCallback(async () => {
    const token = ++seq.current;
    setLoading(true);
    setError(null);
    try {
      const result = await Api.fetchPeople(apiFilters, { sort, dir, page, size: SIZE });
      if (seq.current === token) setData(result);
    } catch (err) {
      if (seq.current === token) setError(err);
    } finally {
      if (seq.current === token) setLoading(false);
    }
  }, [apiFilters, sort, dir, page]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    Api.fetchStatistics().then(setStats).catch(() => setStats(null));
    Api.fetchEventOptions().then((options) => setEvents(options || [])).catch(() => setEvents([]));
  }, []);

  // Any filter or sort change starts again at the first page.
  const applyFilters = useCallback((change) => {
    setFilters((current) => ({ ...current, ...change }));
    setPage(0);
  }, []);

  const setStatus = useCallback((status, on) => {
    setFilters((current) => ({
      ...current,
      statuses: on ? [...current.statuses, status] : current.statuses.filter((s) => s !== status),
    }));
    setPage(0);
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({ search: '', statuses: [], interest: '', hasAccount: '', eventId: '' });
    setSearchInput('');
    setPage(0);
  }, []);

  const submitSearch = useCallback((event) => {
    event.preventDefault();
    applyFilters({ search: searchInput.trim() });
  }, [applyFilters, searchInput]);

  const exportCsv = useCallback(async () => {
    setExporting(true);
    setError(null);
    try {
      const blob = await Api.exportPeopleCsv(apiFilters);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'people.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err);
    } finally {
      setExporting(false);
    }
  }, [apiFilters]);

  const filterCount = filters.statuses.length + (filters.interest ? 1 : 0)
    + (filters.hasAccount !== '' ? 1 : 0) + (filters.eventId ? 1 : 0) + (filters.search ? 1 : 0);
  const eventLabel = (event) => {
    const name = eventTitle(language, event.nameDe, event.nameEn) || `#${event.id}`;
    return event.startsOn ? `${name} — ${event.startsOn}` : name;
  };

  const rows = data?.content || [];
  const totalPages = data?.totalPages || 0;

  return (
    <Container>
      <BackLink to="/admin/dashboard">
        <FormattedMessage id="personAdmin.back" defaultMessage="Back to the dashboard" />
      </BackLink>
      <Header>
        <div>
          <Title><FormattedMessage id="personAdmin.title" defaultMessage="People" /></Title>
          <Subtitle>
            <FormattedMessage id="personAdmin.subtitle"
              defaultMessage="Everyone the program and the invitations know: one person per human, with or without an account." />
          </Subtitle>
        </div>
        <div style={{ display: 'flex', gap: '.6rem', flexWrap: 'wrap' }}>
          <Button type="button" secondary disabled={loading} onClick={load}>
            <RefreshCw size={16} /><FormattedMessage id="personAdmin.refresh" defaultMessage="Refresh" />
          </Button>
          {can(PERMISSIONS.peopleExport) && (
            <Button type="button" disabled={exporting} onClick={exportCsv}>
              <Download size={16} /><FormattedMessage id="personAdmin.exportCsv" defaultMessage="Export CSV" />
            </Button>
          )}
          <Button type="button" onClick={() => setCreating(true)}>
            <UserPlus size={16} /><FormattedMessage id="personAdmin.new.button" defaultMessage="New person" />
          </Button>
        </div>
      </Header>

      {creating && (
        <Overlay>
          <Modal role="dialog" aria-label={t('personAdmin.new.title', 'New person')}
            style={{ maxWidth: '40rem', maxHeight: 'calc(100vh - 2rem)', overflowY: 'auto' }}>
            <ModalTitle><UserPlus size={18} /><FormattedMessage id="personAdmin.new.title" defaultMessage="New person" /></ModalTitle>
            <NewPersonForm
              findMatches={Api.fetchPersonMatches}
              create={Api.createPerson}
              onCreated={(detail) => navigate(`/admin/people/${detail.id}`)}
              onExisting={(candidate) => navigate(`/admin/people/${candidate.personId}`)}
              onCancel={() => setCreating(false)}
            />
          </Modal>
        </Overlay>
      )}

      {stats && (
        <StatBar>
          <StatPill type="button" $accent="#111D4E" $active={filters.hasAccount === ''} onClick={() => applyFilters({ hasAccount: '' })}>
            <StatPillValue>{stats.totalPersons}</StatPillValue>
            <StatPillLabel><FormattedMessage id="personAdmin.kpi.total" defaultMessage="People" /></StatPillLabel>
          </StatPill>
          <StatPill type="button" $accent="#166534" $active={filters.hasAccount === 'true'} onClick={() => applyFilters({ hasAccount: 'true' })}>
            <StatPillValue>{stats.withAccount}</StatPillValue>
            <StatPillLabel><FormattedMessage id="personAdmin.kpi.withAccount" defaultMessage="With account" /></StatPillLabel>
          </StatPill>
          <StatPill type="button" $accent="#92400e" $active={filters.hasAccount === 'false'} onClick={() => applyFilters({ hasAccount: 'false' })}>
            <StatPillValue>{stats.withoutAccount}</StatPillValue>
            <StatPillLabel><FormattedMessage id="personAdmin.kpi.withoutAccount" defaultMessage="Without account" /></StatPillLabel>
          </StatPill>
          <StatPill as="div" $accent="#075985">
            <StatPillValue>{stats.withUpcomingSpot}</StatPillValue>
            <StatPillLabel><FormattedMessage id="personAdmin.kpi.upcomingSpot" defaultMessage="On an upcoming event" /></StatPillLabel>
          </StatPill>
        </StatBar>
      )}

      {error && <Message error role="alert">{errorText(error, t('personAdmin.load.failed', 'Could not load the people.'))}</Message>}

      <Card>
        <CardTitle><FormattedMessage id="personAdmin.filters.title" defaultMessage="Find people" /></CardTitle>
        <form onSubmit={submitSearch} style={{ display: 'flex', gap: '.6rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <Field style={{ minWidth: '18rem', flex: '1 1 18rem' }}>
            <FormattedMessage id="personAdmin.search" defaultMessage="Name or email" />
            <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t('personAdmin.searchPlaceholder', 'e.g. Example or name@example.org')} />
          </Field>
          <Button type="submit"><Search size={16} /><FormattedMessage id="personAdmin.search.action" defaultMessage="Search" /></Button>
          {filterCount > 0 && (
            <Button type="button" secondary onClick={clearFilters}>
              <X size={16} /><FormattedMessage id="personAdmin.filters.clear" defaultMessage="Clear filters" />
            </Button>
          )}
        </form>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(12rem, 1fr))', gap: '.9rem', marginTop: '1rem' }}>
          <Field>
            <FormattedMessage id="personAdmin.filter.interest" defaultMessage="Interest" />
            <Select value={filters.interest} onChange={(e) => applyFilters({ interest: e.target.value })}>
              <option value="">{t('personAdmin.filter.any', 'Any')}</option>
              {INTEREST_VALUES.map(({ interest }) => (
                <option key={interest} value={interest}>{interestLabel(t, interest)}</option>
              ))}
            </Select>
          </Field>
          <Field>
            <FormattedMessage id="personAdmin.filter.account" defaultMessage="Account" />
            <Select value={filters.hasAccount} onChange={(e) => applyFilters({ hasAccount: e.target.value })}>
              <option value="">{t('personAdmin.filter.any', 'Any')}</option>
              <option value="true">{t('personAdmin.filter.withAccount', 'With an account')}</option>
              <option value="false">{t('personAdmin.filter.withoutAccount', 'Without an account')}</option>
            </Select>
          </Field>
          <Field>
            <FormattedMessage id="personAdmin.filter.event" defaultMessage="At an event" />
            <Select value={filters.eventId} onChange={(e) => applyFilters({ eventId: e.target.value })}>
              <option value="">{t('personAdmin.filter.any', 'Any')}</option>
              {events.map((event) => <option key={event.id} value={event.id}>{eventLabel(event)}</option>)}
            </Select>
          </Field>
          <Field>
            <FormattedMessage id="personAdmin.sort.label" defaultMessage="Sort by" />
            <div style={{ display: 'flex', gap: '.4rem' }}>
              <Select value={sort} onChange={(e) => { setSort(e.target.value); setPage(0); }} style={{ flex: 1 }}>
                {SORT_OPTIONS.map((option) => <option key={option.value} value={option.value}>{t(option.id, option.value)}</option>)}
              </Select>
              <Button type="button" secondary style={{ padding: '.6rem' }}
                title={dir === 'asc' ? t('personAdmin.sort.desc', 'Descending') : t('personAdmin.sort.asc', 'Ascending')}
                onClick={() => { setDir(dir === 'asc' ? 'desc' : 'asc'); setPage(0); }}>
                {dir === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </Button>
            </div>
          </Field>
        </div>

        <div style={{ marginTop: '.9rem' }}>
          <div style={{ fontSize: '.82rem', fontWeight: 600, color: '#334155', marginBottom: '.35rem' }}>
            <FormattedMessage id="personAdmin.filter.statuses" defaultMessage="Professional status" />
          </div>
          <Checks>
            {PROFESSIONAL_STATUS_VALUES.map((status) => (
              <CheckLabel key={status}>
                <input type="checkbox" checked={filters.statuses.includes(status)}
                  onChange={(e) => setStatus(status, e.target.checked)} />
                {statusLabel(t, status)}
              </CheckLabel>
            ))}
          </Checks>
        </div>

        {filterCount > 0 && (
          <FilterChip style={{ marginTop: '.9rem', marginBottom: 0 }}>
            <FormattedMessage id="personAdmin.filters.active" defaultMessage="{count, plural, one {# filter} other {# filters}} active"
              values={{ count: filterCount }} />
            <ChipClear type="button" onClick={clearFilters} aria-label={t('personAdmin.filters.clear', 'Clear filters')}><X size={14} /></ChipClear>
          </FilterChip>
        )}
      </Card>

      <Card>
        {loading && !data ? (
          <Empty><FormattedMessage id="personAdmin.loading" defaultMessage="Loading people…" /></Empty>
        ) : rows.length === 0 ? (
          <Empty><FormattedMessage id="personAdmin.empty" defaultMessage="No person matches these filters." /></Empty>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.85rem' }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: '#475569' }}>
                    <Th><FormattedMessage id="personAdmin.col.name" defaultMessage="Person" /></Th>
                    <Th><FormattedMessage id="personAdmin.col.email" defaultMessage="Email" /></Th>
                    <Th><FormattedMessage id="personAdmin.col.institution" defaultMessage="Institution" /></Th>
                    <Th><FormattedMessage id="personAdmin.col.country" defaultMessage="Country" /></Th>
                    <Th><FormattedMessage id="personAdmin.col.statuses" defaultMessage="Status" /></Th>
                    <Th><FormattedMessage id="personAdmin.col.interests" defaultMessage="Interests" /></Th>
                    <Th><FormattedMessage id="personAdmin.col.account" defaultMessage="Account" /></Th>
                    <Th><FormattedMessage id="personAdmin.col.origin" defaultMessage="Origin" /></Th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id} style={{ borderTop: '1px solid #eef2f7' }}>
                      <Td><Link to={`/admin/people/${row.id}`} style={{ fontWeight: 700, color: '#111D4E' }}>{row.programName}</Link></Td>
                      <Td>{row.contactEmail || '—'}</Td>
                      <Td>{row.institution || '—'}</Td>
                      <Td>{row.country || '—'}</Td>
                      <Td>{(row.professionalStatuses || []).map((s) => statusLabel(t, s)).join(', ') || '—'}</Td>
                      <Td>{(row.interests || []).map((i) => interestLabel(t, i)).join(', ') || '—'}</Td>
                      <Td>
                        {row.hasAccount
                          ? <FormattedMessage id="personAdmin.account.yes" defaultMessage="Account" />
                          : <FormattedMessage id="personAdmin.account.no" defaultMessage="Person" />}
                      </Td>
                      <Td>{originLabel(t, row.origin)}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem', marginTop: '1rem', flexWrap: 'wrap' }}>
              <Button type="button" secondary disabled={loading || page <= 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
                <ChevronLeft size={16} /><FormattedMessage id="personAdmin.page.prev" defaultMessage="Previous" />
              </Button>
              <span style={{ fontSize: '.82rem', color: '#475569' }}>
                <FormattedMessage id="personAdmin.page.of"
                  defaultMessage="Page {page} of {pages} — {total, plural, one {# person} other {# people}}"
                  values={{ page: (data?.page ?? 0) + 1, pages: Math.max(totalPages, 1), total: data?.totalElements ?? rows.length }} />
              </span>
              <Button type="button" secondary disabled={loading || page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}>
                <FormattedMessage id="personAdmin.page.next" defaultMessage="Next" /><ChevronRight size={16} />
              </Button>
            </div>
          </>
        )}
      </Card>
    </Container>
  );
};

const Th = ({ children }) => (
  <th style={{ padding: '.5rem .6rem', fontWeight: 700, borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap' }}>{children}</th>
);
const Td = ({ children }) => <td style={{ padding: '.5rem .6rem', verticalAlign: 'top' }}>{children}</td>;

export default PersonList;
